import { FC, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import dayjs from 'dayjs';
import styles from './BodyMapMonthCalendar.module.scss';
import { INTENSITY_LEGEND, getIntensityColor } from '../../../shared/bodyMap/bodyMapScale';
import LeopartLoader from '../../../shared/LeopartLoader/LeopartLoader';
import { ChartErrorState } from '../AthleteInjuryCharts/charts/_shared/ChartErrorState';

interface BodyMapDocument {
  _id: string;
  userId: string;
  date: string;
  complaints: { zone: string; intensity: number }[];
}

interface Props {
  athleteId: string;
  selectedDate: string;
  onDateChange: (date: string) => void;
}

const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

/** Classi legenda in ordine fisso Lieve/Moderata/Intensa, coerenti con INTENSITY_LEGEND. */
const LEGEND_DOT_CLASSNAMES = ['legendDotLieve', 'legendDotModerata', 'legendDotIntensa'] as const;

const BodyMapMonthCalendar: FC<Props> = ({ athleteId, selectedDate, onDateChange }) => {
  const endpoints = useEndpoints();
  const today = dayjs().format('YYYY-MM-DD');

  // currentMonth è YYYY-MM (mese visualizzato nel calendario)
  const [currentMonth, setCurrentMonth] = useState<string>(() =>
    dayjs(selectedDate).format('YYYY-MM')
  );

  const monthStart = dayjs(`${currentMonth}-01`);
  const startDate = monthStart.format('YYYY-MM-DD');
  const endDate = monthStart.endOf('month').format('YYYY-MM-DD');

  const { data: docs, isLoading, isError, refetch } = useQuery<BodyMapDocument[]>({
    queryKey: ['bodymap-month', athleteId, currentMonth],
    queryFn: async () => {
      const { data } = await endpoints.getBodyMapAthlete(athleteId, {
        startDate,
        endDate,
        limit: 100,
      });
      return data as BodyMapDocument[];
    },
    enabled: !!athleteId,
    staleTime: 1000 * 60 * 5,
  });

  // Mappa date → max intensity del giorno
  const dayData = useMemo(() => {
    const map = new Map<string, { count: number; maxIntensity: number }>();
    for (const doc of docs ?? []) {
      const d = dayjs(doc.date).format('YYYY-MM-DD');
      const maxInt = Math.max(...doc.complaints.map((c) => c.intensity), 0);
      map.set(d, { count: doc.complaints.length, maxIntensity: maxInt });
    }
    return map;
  }, [docs]);

  // Costruisce la griglia del mese (partendo da lunedì)
  const calendarDays = useMemo(() => {
    const firstDay = monthStart.startOf('month');
    // dayjs day(): 0=Dom, 1=Lun, ..., 6=Sab → convertiamo a lunedì-based
    const startOffset = (firstDay.day() + 6) % 7;
    const daysInMonth = monthStart.daysInMonth();
    const cells: (string | null)[] = Array(startOffset).fill(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(monthStart.date(d).format('YYYY-MM-DD'));
    }
    // Riempi con null fino a multiplo di 7
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [currentMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePrevMonth = () => {
    setCurrentMonth(dayjs(`${currentMonth}-01`).subtract(1, 'month').format('YYYY-MM'));
  };
  const handleNextMonth = () => {
    const next = dayjs(`${currentMonth}-01`).add(1, 'month');
    if (next.format('YYYY-MM') <= dayjs().format('YYYY-MM')) {
      setCurrentMonth(next.format('YYYY-MM'));
    }
  };
  const isNextDisabled = currentMonth >= dayjs().format('YYYY-MM');

  return (
    <div className={styles.container}>
      {/* Header mese */}
      <div className={styles.header}>
        <span className={styles.title}>Calendario segnalazioni</span>
        <div className={styles.monthNav}>
          <button className={styles.navBtn} onClick={handlePrevMonth} title="Mese precedente">
            <CaretLeftIcon size={15} />
          </button>
          <span className={styles.monthLabel}>
            {monthStart.format('MMMM YYYY')}
          </span>
          <button
            className={`${styles.navBtn} ${isNextDisabled ? styles.navBtnDisabled : ''}`}
            onClick={handleNextMonth}
            disabled={isNextDisabled}
            title="Mese successivo"
          >
            <CaretRightIcon size={15} />
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className={styles.stateWrap}>
          <LeopartLoader size="sm" />
        </div>
      ) : isError ? (
        <div className={styles.stateWrap}>
          <ChartErrorState onRetry={() => refetch()} />
        </div>
      ) : (
        <>
          {/* Intestazioni giorni */}
          <div className={styles.grid}>
            {DAY_LABELS.map((d) => (
              <div key={d} className={styles.dayHeader}>{d}</div>
            ))}

            {/* Celle giorni */}
            {calendarDays.map((date, idx) => {
              if (!date) return <div key={`empty-${idx}`} className={styles.emptyCell} />;

              const isToday = date === today;
              const isSelected = date === selectedDate;
              const isFuture = date > today;
              const info = dayData.get(date);
              const hasComplaints = !!info;

              return (
                <div
                  key={date}
                  className={[
                    styles.dayCell,
                    isToday ? styles.dayCellToday : '',
                    isSelected ? styles.dayCellSelected : '',
                    isFuture ? styles.dayCellFuture : '',
                    hasComplaints ? styles.dayCellHasComplaints : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => !isFuture && onDateChange(date)}
                  title={hasComplaints ? `${info.count} fastidi segnalati` : undefined}
                >
                  <span className={styles.dayNumber}>{dayjs(date).date()}</span>
                  {hasComplaints && (
                    <span
                      className={styles.complaintDot}
                      style={{ background: getIntensityColor(info.maxIntensity) }}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* Legenda */}
          <div className={styles.legend}>
            {INTENSITY_LEGEND.map((item, i) => (
              <div key={item.label} className={styles.legendItem}>
                <span className={`${styles.legendDot} ${styles[LEGEND_DOT_CLASSNAMES[i]]}`} />
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default BodyMapMonthCalendar;
